import { useEffect, useRef, useState } from "react";
import { Button, Card, Flex, Heading, Spinner, Text } from "@radix-ui/themes";
import { useSearchParams } from "react-router-dom";

import { linkSlackIdentity, type SlackLinkResponse } from "../api/integrations";
import { authClient } from "../lib/auth";

type LinkStatus = "checking" | "signin" | "linking" | "linked" | "error";

function currentAppPath(): string {
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  const fullPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (base && fullPath.startsWith(`${base}/`)) return fullPath.slice(base.length);
  if (base && fullPath === base) return "/";
  return fullPath || "/auth/slack-link";
}

function authCallbackUrl(): string {
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  return `${window.location.origin}${base}/auth/callback`;
}

export default function SlackLinkPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [status, setStatus] = useState<LinkStatus>("checking");
  const [error, setError] = useState("");
  const [link, setLink] = useState<SlackLinkResponse | null>(null);
  const linkStarted = useRef(false);

  useEffect(() => {
    if (!token) {
      setError("Missing Slack link token.");
      setStatus("error");
      return;
    }
    authClient.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        setStatus("signin");
        return;
      }
      if (linkStarted.current) return;
      linkStarted.current = true;
      setStatus("linking");
      linkSlackIdentity(token)
        .then((response) => {
          setLink(response);
          setStatus("linked");
        })
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : String(err));
          setStatus("error");
        });
    });
  }, [token]);

  const handleSignIn = async () => {
    sessionStorage.setItem("thread.post_auth_redirect", currentAppPath());
    await authClient.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: authCallbackUrl() },
    });
  };

  const label = link?.thread_display_name || link?.thread_email || link?.thread_user_id;

  return (
    <Flex
      align="center"
      justify="center"
      style={{ minHeight: "100vh", padding: 24, background: "var(--color-background)" }}
    >
      <Card size="4" style={{ width: "min(440px, 100%)", padding: 32 }}>
        <Flex direction="column" gap="4">
          <Heading size="6">Connect Slack to Thread</Heading>

          {status === "checking" || status === "linking" ? (
            <Flex align="center" gap="3">
              <Spinner />
              <Text color="gray">{status === "checking" ? "Checking sign-in..." : "Linking account..."}</Text>
            </Flex>
          ) : null}

          {status === "signin" ? (
            <>
              <Text color="gray">
                Sign in to Thread/Bobbin to confirm this Slack account before Jenny can show work status.
              </Text>
              <Button size="3" onClick={handleSignIn} style={{ cursor: "pointer" }}>
                Continue with Google
              </Button>
            </>
          ) : null}

          {status === "linked" ? (
            <Text color="green">
              Linked. Jenny can now recognize this Slack account as {label ? `Thread user ${label}` : "your Thread login"}.
            </Text>
          ) : null}

          {status === "error" ? (
            <>
              <Text color="red">Jenny could not link this Slack account.</Text>
              <Text size="2" color="gray" style={{ wordBreak: "break-word" }}>
                {error}
              </Text>
            </>
          ) : null}
        </Flex>
      </Card>
    </Flex>
  );
}
